import React from "react";

import Tools from "../tools";
import Scene from "../scene";
import Picker from "../picker";
import { v4 } from "uuid";
import { TinyColor } from "@ctrl/tinycolor";

import "./app.css";

function loadImage(src) {
  const image = new Image();
  image.src = src;
  return image;
}

function rgbToHex(r, g, b) {
  if (r > 255 || g > 255 || b > 255) return null;
  var hex = ((r << 16) | (g << 8) | b).toString(16);
  return "#" + ("000000" + hex).slice(-6);
}

export default class App extends React.Component {

  constructor(props) {
    super(props);

    this.scene = null;

    this.skinLoad = document.getElementById("mc-skin-editor").dataset.skin || localStorage.getItem("skin-storage") || "/assets/default_skin.png";


    this.image = loadImage(this.skinLoad);

    this.state = {
      color: "#000000",
      hiddenParts: [],
      showBody: true,
      showOverlay: true,
      image: this.image,
      model: "steve",
      penMode: "pen", // eraser, fill, picker
      lastPen: "pen", // to return after use picker
      isMirrored: false,
      history: [{ id: 1, action: this.image.src, name: "init" }],
      historyId: 1,
    };
  }

  componentDidMount = () => {
    localStorage.setItem("skin-storage", this.skinLoad);
  }

  onColorChange = (color) => {
    const valid = new TinyColor(color).isValid;
    if (!valid) return;
    this.setState({ color });
    this.scene?.setColor(color);
  };

  onPartToggle = (part) => {
    const hiddenParts = this.state.hiddenParts.includes(part)
      ? this.state.hiddenParts.filter((item) => item !== part)
      : this.state.hiddenParts.concat(part);
    this.setState({ hiddenParts });
    this.scene?.toggleParts(
      hiddenParts,
      this.state.showBody,
      this.state.showOverlay
    );
  };

  onToggleBody = () => {
    this.setState({ showBody: !this.state.showBody });
    this.scene?.toggleParts(
      this.state.hiddenParts,
      this.state.showBody,
      this.state.showOverlay
    );
  };

  onToggleOverlay = () => {
    this.setState({ showOverlay: !this.state.showOverlay });
    this.scene?.toggleParts(
      this.state.hiddenParts,
      this.state.showBody,
      this.state.showOverlay
    );
  };

  onModelChange = (model) => {
    this.setState({ model });
    this.scene?.setModel(model);
  };

  onImageImport = (image) => {
    const texture = loadImage(image);
    this.setState({ image: texture });
    this.scene?.setTexture(texture);
    setTimeout(() => this.onSaveHistory("import"), 300);
  };

  onPenSwitch = (mode) => {
    this.setState({
      lastPen:
        this.state.penMode === "picker"
          ? this.state.lastPen
          : this.state.penMode,
      penMode: mode,
    });
  };

  onMirrorToggle = () => {
    this.setState({ isMirrored: !this.state.isMirrored });
  };

  onColorPick = (data) => {
    const color = rgbToHex(data[0], data[1], data[2]);
    this.setState({
      color,
      penMode: this.state.lastPen,
    });
    this.scene?.setColor(color);
  };

  onSaveHistory = (name) => {
    const canvas = document.getElementById("drawing-canvas");
    const action = canvas.toDataURL();
    const id = v4();
    const history = JSON.parse(JSON.stringify(this.state.history));
    const lastAction = history[history.length - 1];
    const historyId = this.state.historyId;

    // Write new history on change
    if (lastAction.id !== this.state.historyId) {
      const index = history.findIndex((item) => item.id === historyId);
      history.splice(index + 1);
    }

    // Limit history length to 15
    if (history.length >= 15) {
      history.shift();
    }

    this.setState({
      historyId: id,
      history: history.concat({ id, action, name: name || this.state.penMode }),
    });

    localStorage.setItem("skin-storage", action);
  };

  onUndo = () => {
    const historyId = this.state.historyId;
    const index = this.state.history.findIndex((item) => item.id === historyId);
    if (index > -1 && this.state.history[index - 1]) {
      const action = this.state.history[index - 1];
      const texture = loadImage(action.action);
      this.setState({ image: texture, historyId: action.id });
      this.scene?.setTexture(texture);
    }
  };

  onRedo = () => {
    const historyId = this.state.historyId;
    const index = this.state.history.findIndex((item) => item.id === historyId);
    if (index > -1 && this.state.history[index + 1]) {
      const action = this.state.history[index + 1];
      const texture = loadImage(action.action);
      this.setState({ image: texture, historyId: action.id });
      this.scene?.setTexture(texture);
    }
  };

  onZoomIn = () => {
    this.scene?.zoomIn();
  };

  onZoomOut = () => {
    this.scene?.zoomOut();
  };

  onLighten = () => {
    const color = new TinyColor(this.state.color).lighten(5).toHexString();
    this.setState({ color });
    this.scene?.setColor(color);
  };

  onDarken = () => {
    const color = new TinyColor(this.state.color).darken(5).toHexString();
    this.setState({ color });
    this.scene?.setColor(color);
  };

  onSkinReset = () => {
    const confirm = window.confirm(
      "All work will be lost! Are you sure you want to proceed?"
    );
    if (confirm) window.location.reload();
  };

  render() {
    this.scene?.toggleParts(
      this.state.hiddenParts,
      this.state.showBody,
      this.state.showOverlay
    );

    return (
      <div className="app">
        <div className="container-xl">
          <div className="skin-editor">
            <div className="d-flex align-items-stretch app-layout">
              <Tools
                penMode={this.state.penMode}
                isMirrored={this.state.isMirrored}
                onPenSwitch={this.onPenSwitch}
                onUndo={this.onUndo}
                onRedo={this.onRedo}
                onZoomIn={this.onZoomIn}
                onZoomOut={this.onZoomOut}
                onDarken={this.onDarken}
                onLighten={this.onLighten}
                onMirrorToggle={this.onMirrorToggle}
                onSkinReset={this.onSkinReset}
              />
              <Scene
                texture={this.state.image}
                penMode={this.state.penMode}
                isMirrored={this.state.isMirrored}
                ref={(scene) => (this.scene = scene)}
                onColorPick={this.onColorPick}
                onSaveHistory={this.onSaveHistory}
              />
              <Picker
                color={this.state.color}
                hiddenParts={this.state.hiddenParts}
                showBody={this.state.showBody}
                showOverlay={this.state.showOverlay}
                model={this.state.model}
                onColorChange={this.onColorChange}
                onPartToggle={this.onPartToggle}
                onModelChange={this.onModelChange}
                onImageImport={this.onImageImport}
                onToggleBody={this.onToggleBody}
                onToggleOverlay={this.onToggleOverlay}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}
